import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'
import { ArrowClockwise, CheckCircle, Clock, XCircle } from '@phosphor-icons/react'
import useSessionStore from 'Context/useSessionStore'
import { usePayment } from 'Hooks'
import { getMe } from 'Services'
import { DASHBAORD_ROUTER, PRICING_ROUTER } from 'constant/router'
import { CURRENT_USER_STORAGE } from 'Utils/const/localstorage'
import { clearPendingCheckout } from 'Utils/pendingCheckout'
import { GET_COLOR_BY_THEME } from 'constant/colors'

const POLL_INTERVAL_MS = 2500
const MAX_ATTEMPTS = 12 // ~30s of polling before we show the "still working" state

// Shown when Stripe redirects back to /pricing after checkout. Confirms the
// payment server-side (which also provisions the plan) and gives the user an
// explicit success / still-working / canceled state instead of dropping them
// back onto the plan cards with no acknowledgement.
const CheckoutStatus = ({ outcome, sessionId }) => {
  const navigate = useNavigate()
  const { user, updateUser } = useSessionStore((state) => ({ user: state.user, updateUser: state.updateUser }))
  const { getCheckoutStatusHook } = usePayment()

  // verifying | active | working | canceled
  const [phase, setPhase] = useState(outcome === 'canceled' ? 'canceled' : 'verifying')
  const [trialEnd, setTrialEnd] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)

  const timer = useRef(null)
  const primaryColor = GET_COLOR_BY_THEME({ theme: user?.theme ?? true }).PRIMARY

  const refreshUser = async () => {
    try {
      const { data } = await getMe()
      localStorage.setItem(CURRENT_USER_STORAGE, JSON.stringify({ ...user, ...data }))
      await updateUser({ ...user, ...data })
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    if (outcome === 'canceled') {
      clearPendingCheckout()
      return
    }

    let cancelled = false
    let attempts = 0
    setPhase('verifying')

    const finishActive = async (data) => {
      clearPendingCheckout()
      setTrialEnd(data?.trial_end || null)
      setPhase('active')
      await refreshUser()
      if (!cancelled) timer.current = setTimeout(() => navigate(DASHBAORD_ROUTER), POLL_INTERVAL_MS)
    }

    const poll = async () => {
      if (cancelled) return

      if (!sessionId) {
        setPhase('working')
        return
      }

      attempts += 1
      const res = await getCheckoutStatusHook({ sessionId })
      if (cancelled) return

      const status = res?.data?.status

      if (res.success && status === 'active') return finishActive(res.data)

      if (res.success && (status === 'canceled' || status === 'expired')) {
        clearPendingCheckout()
        setPhase('canceled')
        return
      }

      if (attempts >= MAX_ATTEMPTS) {
        setPhase('working')
        return
      }

      timer.current = setTimeout(poll, POLL_INTERVAL_MS)
    }

    poll()

    return () => {
      cancelled = true
      if (timer.current) clearTimeout(timer.current)
    }
  }, [outcome, sessionId, reloadKey])

  const retry = () => setReloadKey((k) => k + 1)

  return (
    <div className="landing_img_text_bg pb-5">
      <div className="containers pricing pt-4">
        <div className="row justify-content-center py-5">
          <div className="col-12 col-md-7 col-lg-6">
            <div className="card p-5 text-center">
              {phase === 'verifying' && (
                <>
                  <div className="d-flex justify-content-center mb-4">
                    <div className="spinner" />
                  </div>
                  <h3 className="mb-2">Confirming your payment…</h3>
                  <p className="text-quinary m-0">This only takes a few seconds.</p>
                </>
              )}

              {phase === 'active' && (
                <>
                  <div className="d-flex justify-content-center mb-3">
                    <CheckCircle size={64} weight="fill" color={primaryColor} />
                  </div>
                  <h3 className="mb-2">You&apos;re on Professional</h3>
                  <p className="text-quinary mb-4">
                    {trialEnd
                      ? `Your 30-day free trial has started — no charge until ${DateTime.fromISO(trialEnd).toFormat('LLL dd, yyyy')}.`
                      : 'Your subscription is now active.'}
                  </p>
                  <div className="row justify-content-center">
                    <div className="col-auto">
                      <button type="button" className="btn btn-primary" onClick={() => navigate(DASHBAORD_ROUTER)}>
                        Go to dashboard
                      </button>
                    </div>
                  </div>
                </>
              )}

              {phase === 'working' && (
                <>
                  <div className="d-flex justify-content-center mb-3">
                    <Clock size={64} color={primaryColor} />
                  </div>
                  <h3 className="mb-2">Payment received</h3>
                  <p className="text-quinary mb-4">
                    Activating your subscription is taking a little longer than usual. It will finish
                    automatically — you can keep working and your plan will update shortly.
                  </p>
                  <div className="row justify-content-center gy-2">
                    <div className="col-auto">
                      <button type="button" className="btn btn-secondary" onClick={retry}>
                        <ArrowClockwise className="me-2" size={20} />
                        Check again
                      </button>
                    </div>
                    <div className="col-auto">
                      <button type="button" className="btn btn-primary" onClick={() => navigate(DASHBAORD_ROUTER)}>
                        Go to dashboard
                      </button>
                    </div>
                  </div>
                </>
              )}

              {phase === 'canceled' && (
                <>
                  <div className="d-flex justify-content-center mb-3">
                    <XCircle size={64} color={primaryColor} />
                  </div>
                  <h3 className="mb-2">Checkout canceled</h3>
                  <p className="text-quinary mb-4">
                    You haven&apos;t been charged. You can pick a plan whenever you&apos;re ready.
                  </p>
                  <div className="row justify-content-center">
                    <div className="col-auto">
                      <button type="button" className="btn btn-primary" onClick={() => navigate(PRICING_ROUTER)}>
                        Back to plans
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CheckoutStatus
